import { useEffect, useRef } from 'react'
import { useSetAtom } from 'jotai'
import { authenticationApi, authenticationState, customerState, persistAuthData, ssoLoadingState, type TAuthentication, type TCustomer } from '@repo/unicity'

type SsoPayload = {
    baId: string
    country: string
    token: string
}

const exchangeSsoToken = async (ssToken: string) => {
    const remote = await authenticationApi.autoLogin({ token: ssToken })
    const payload = remote.data.payload as SsoPayload

    const onself = await authenticationApi.onself({
        countryCode3alpha: payload.country,
        baId: payload.baId,
        token: payload.token
    })

    return {
        auth: payload,
        customer: onself.data.ushop.profile as TCustomer
    }
}

export const useSpotlightSsoEntry = () => {
    const setAuthentication = useSetAtom(authenticationState)
    const setCustomer = useSetAtom(customerState)
    const setSsoLoading = useSetAtom(ssoLoadingState)
    const exchangedTokenRef = useRef('')

    useEffect(() => {
        const qs = new URLSearchParams(window.location.search)
        const ss = qs.get('ss')
        if (!ss || exchangedTokenRef.current === ss) return

        exchangedTokenRef.current = ss
        setSsoLoading(true)

        const run = async () => {
            try {
                const { auth, customer } = await exchangeSsoToken(ss)
                const authData: TAuthentication = {
                    baId: auth.baId,
                    token: auth.token,
                    customer: { href: customer.href },
                    expired: false,
                    href: customer.href,
                    log_id: null,
                    duration: '',
                    loginTokenSecondsLeft: 0
                }

                setAuthentication(authData)
                setCustomer(customer)
                persistAuthData(authData, customer)

                qs.delete('ss')
                const search = qs.toString()
                window.history.replaceState(null, '', window.location.pathname + (search ? `?${search}` : '') + window.location.hash)
            } catch (error) {
                console.error('Spotlight SSO exchange failed:', error)
            } finally {
                setSsoLoading(false)
            }
        }

        void run()
    }, [setAuthentication, setCustomer, setSsoLoading])
}
